import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { roleLabel } from '@technohull/shared';
import { useAuth } from '../auth/AuthContext.js';
import { ADMIN_PERMS } from '../app/nav.js';
import { MQ, useMediaQuery } from '../lib/media.js';
import { useSettings, type DateFmt, type PrefsSync, type ThemePref } from '../settings/SettingsContext.js';
import { sampleDate, useDateFormatter } from '../lib/format.js';
import { LOGO_ORDER, LOGO_VARIANTS, logoDef, type LogoVariant } from '../lib/logos.js';
import { PALETTES, PALETTE_ORDER, paletteId, type PaletteId } from '../lib/palettes.js';
import { UI_THEMES, UI_THEME_ORDER, uiThemeId, type UiThemeId } from '../lib/themes.js';
import { PageHeader } from '../components/ui.js';
import { Avatar } from '../components/Avatar.js';
import { ProfileModal } from '../components/ProfileModal.js';
import { MyGoogleAccount } from '../components/GoogleAccount.js';
import { ThemePreview } from '../components/ThemePreview.js';
import { IconCheck } from '../components/icons.js';
import { MyTickets } from '../components/Tickets.js';

const REGIONS: { id: string; label: string }[] = [
  { id: 'en-GB', label: 'United Kingdom (English)' },
  { id: 'en-US', label: 'United States (English)' },
  { id: 'el-GR', label: 'Greece (Ελληνικά)' },
  { id: 'it-IT', label: 'Italy (Italiano)' },
  { id: 'de-DE', label: 'Germany (Deutsch)' },
  { id: 'fr-FR', label: 'France (Français)' },
  { id: 'es-ES', label: 'Spain (Español)' },
];

// labels are built from a live sample so they always track the chosen region.
const DATE_FORMATS: { id: DateFmt; name: string }[] = [
  { id: 'locale', name: 'Regional default' },
  { id: 'DD MMM YYYY', name: 'Day Month Year' },
  { id: 'DD/MM/YYYY', name: 'Day / Month / Year' },
  { id: 'MM/DD/YYYY', name: 'Month / Day / Year' },
  { id: 'YYYY-MM-DD', name: 'Year-Month-Day (ISO)' },
];

const MODES: { id: ThemePref; label: string }[] = [
  { id: 'light', label: 'Light' },
  { id: 'dark', label: 'Dark' },
  { id: 'auto', label: 'Automatic' },
];

// what the line under the title says about your changes. While a save is in flight it keeps the last
// settled message, so the status region doesn't chatter on every click. 'local' is kept on this device
// (a reload included) and sent again until it gets through; 'refused' isn't, so it doesn't promise that.
const SYNC_COPY: Record<Exclude<PrefsSync, 'saving'>, string> = {
  idle: 'Changes apply straight away and follow your account to other devices.',
  saved: 'Saved to your account.',
  local: "Saved on this device for now. Your account didn't respond, so it will be sent again, and your other devices will see it once it gets through.",
  refused: "Your account didn't accept one of your changes. It applies here for now, but it won't reach your other devices.",
};

const PALETTE_GROUPS: { id: 'brand' | 'more'; label: string }[] = [
  { id: 'brand', label: 'ROLODEX colours' },
  { id: 'more', label: 'More colours' },
];

// a section card: an h2 under the page's h1, then its rows. `anchor` is its address on the page
// (/settings#tickets), for a link straight to it.
function Card({ title, className, anchor, children }: { title: string; className?: string; anchor?: string; children: ReactNode }) {
  const id = useId();
  return (
    <section id={anchor} className={`settings-card${className ? ` ${className}` : ''}`} aria-labelledby={id}>
      <h2 className="settings-card-title" id={id}>{title}</h2>
      {children}
    </section>
  );
}

// a row of choices: a native radio group in a fieldset, so the legend names it and the arrow keys move
// through it for free. The hint sits under the legend and the control under the hint, left-aligned.
function ChoiceRow({ legend, current, hint, children }: { legend: string; current?: string; hint?: ReactNode; children: ReactNode }) {
  const hintId = useId();
  return (
    <fieldset className="settings-row" aria-describedby={hint ? hintId : undefined}>
      <legend className="settings-label">
        {legend}
        {/* phones show the tiles as bare swatches, so the legend names the current one */}
        {current && <span className="settings-cur"> · {current}</span>}
      </legend>
      {hint && <p className="settings-hint" id={hintId}>{hint}</p>}
      {children}
    </fieldset>
  );
}

// a row whose control a theme has switched off: the label and a plain note in its place, never a
// disabled control with no explanation.
function NoteRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="settings-row">
      <p className="settings-label">{label}</p>
      <p className="settings-note">{children}</p>
    </div>
  );
}

/** the segmented control as native radios: same look as .seg, but a real radio group (arrow keys,
 *  one Tab stop, checked state), so it can sit in a fieldset. */
function SegRadio<T extends string>({ name, value, options, onChange }: {
  name: string;
  value: T;
  options: { id: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="seg seg-radio">
      {options.map((o) => (
        <label key={o.id} className={`seg-btn${value === o.id ? ' on' : ''}`}>
          <input type="radio" className="sr-only" name={name} value={o.id} checked={value === o.id} onChange={() => onChange(o.id)} />
          {o.label}
        </label>
      ))}
    </div>
  );
}

function ThemeTiles({ value, mode, palette, onChange }: {
  value: UiThemeId;
  mode: 'light' | 'dark';
  palette: PaletteId;
  onChange: (v: UiThemeId) => void;
}) {
  const ids = useId();
  // the device's own high-contrast request is only pointed out, never acted on by itself
  const moreContrast = useMediaQuery('(prefers-contrast: more)');
  return (
    <div className="ttiles">
      {UI_THEME_ORDER.map((id) => {
        const def = UI_THEMES[id];
        const on = value === id;
        const descId = `${ids}-${id}`;
        return (
          <label key={id} className={`ttile${on ? ' sel' : ''}`}>
            {/* named by the tile's title alone; the purpose is its description */}
            <input type="radio" className="sr-only" name="ui-theme" value={id} checked={on} aria-labelledby={`${descId}-name`} aria-describedby={descId} onChange={() => onChange(id)} />
            <ThemePreview theme={id} mode={mode} palette={palette} />
            <span className="ttile-name" id={`${descId}-name`}>
              {def.label}
              {id === 'evolved' && <span className="ttile-tag">Default</span>}
              {on && <IconCheck className="ttile-check" size={16} />}
            </span>
            <span className="ttile-purpose" id={descId}>
              {def.purpose}
              {id === 'contrast' && moreContrast && <span className="ttile-device"> Your device asks for more contrast.</span>}
            </span>
          </label>
        );
      })}
      {/* phones show the tiles three across with their names only; the chosen one's purpose sits under
          them (screen readers already hear each purpose as the tile's description) */}
      <p className="ttiles-current" aria-hidden="true">{UI_THEMES[value].purpose}</p>
    </div>
  );
}

export function Settings() {
  const { me, can, logout } = useAuth();
  const {
    prefs, resolvedTheme, uiTheme, sync,
    setTheme, setUiTheme, setRegion, setDateFormat, setCursorHalo, setLogo, setHideHeaders, setCellHistory, setHoverTips, setPalette,
  } = useSettings();
  const [editProfile, setEditProfile] = useState(false);
  const canHover = useMediaQuery(MQ.hover); // the cursor halo only ever draws for a mouse or trackpad
  const ids = useId();
  const settled = useRef<Exclude<PrefsSync, 'saving'>>('idle');
  if (sync !== 'saving') settled.current = sync;
  // a link to a card (the user menu's Tickets: /settings#tickets) brings it into view
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(decodeURIComponent(hash.slice(1)));
    el?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [hash]);
  if (!me) return null;
  const isAdmin = ADMIN_PERMS.some((p) => can(p));

  const themeId = uiThemeId(prefs.uiTheme);
  const palette = paletteId(prefs.palette);
  const logo = (Object.hasOwn(LOGO_VARIANTS, prefs.logo) ? prefs.logo : 'silver') as LogoVariant;
  const colourHint =
    themeId === 'contrast' ? 'Colours the frame and the accent. Text and lines stay at full contrast.'
      : 'Colours the frame and the accent, and tints the whole app.';

  // a few representative dates, shown in the chosen format so the choice is tangible.
  const now = new Date();
  const soon = new Date(now.getTime() + 1000 * 60 * 60 * 24 * 23);
  const df = useDateFormatter();

  const appearance = (
    <Card title="Appearance">
      <ChoiceRow legend="Theme" hint="Changes the whole look: frame, type, corners and depth.">
        <ThemeTiles value={themeId} mode={resolvedTheme} palette={palette} onChange={setUiTheme} />
      </ChoiceRow>

      <ChoiceRow legend="Light or dark" hint={<>Automatic switches to dark from 19:00 to 07:00. Showing {resolvedTheme} now.</>}>
        <SegRadio name="mode" value={prefs.theme} options={MODES} onChange={setTheme} />
      </ChoiceRow>

      {uiTheme.palette === 'full' ? (
        <ChoiceRow legend="Colour" current={PALETTES[palette].label} hint={colourHint}>
          {PALETTE_GROUPS.map((g) => (
            <div key={g.id} className="pal-group" role="group" aria-labelledby={`${ids}-pal-${g.id}`}>
              <p className="pal-group-hd" id={`${ids}-pal-${g.id}`}>{g.label}</p>
              <div className="pal-choices">
                {PALETTE_ORDER.filter((id) => PALETTES[id].group === g.id).map((id) => (
                  <label key={id} className={`pal-choice${palette === id ? ' sel' : ''}`} title={`${PALETTES[id].label} · ${PALETTES[id].blurb}`}>
                    <input type="radio" className="sr-only" name="palette" value={id} checked={palette === id} onChange={() => setPalette(id)} />
                    <span className="pal-sw" style={{ background: PALETTES[id].swatch }}><span className="pal-dot" style={{ background: PALETTES[id].accent }} /></span>
                    <span className="pal-name">{PALETTES[id].label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </ChoiceRow>
      ) : (
        <NoteRow label="Colour">
          {uiTheme.label} uses its own black, white and magenta. Your colour, {PALETTES[palette].label}, comes back when you switch theme.
        </NoteRow>
      )}

      {uiTheme.logoPicker ? (
        <ChoiceRow legend="Logo" current={LOGO_VARIANTS[logo].label} hint="Your logo colourway. Silver is the standard one. It also sets the browser-tab icon.">
          <div className="logo-choices">
            {LOGO_ORDER.map((id) => (
              <label key={id} className={`logo-choice${logo === id ? ' sel' : ''}`} title={LOGO_VARIANTS[id].label}>
                <input type="radio" className="sr-only" name="logo" value={id} checked={logo === id} onChange={() => setLogo(id)} />
                <img src={LOGO_VARIANTS[id].src} alt="" width={46} height={33} />
                <span className="logo-name">{LOGO_VARIANTS[id].label}</span>
              </label>
            ))}
          </div>
        </ChoiceRow>
      ) : (
        <NoteRow label="Logo">
          {uiTheme.label} draws the mark in white. Your logo colourway, {logoDef(logo).label}, still sets the browser-tab icon and comes back when you switch theme.
        </NoteRow>
      )}
    </Card>
  );

  return (
    <>
      <PageHeader title="Settings" />
      {/* outside the title block, so it stays when page titles are hidden */}
      <p className="settings-sync" role="status" aria-live="polite">{SYNC_COPY[settled.current]}</p>
      <div className="settings-page">
        <div className="settings-wrap">
          <div className="settings-col">{appearance}</div>

          <div className="settings-col">
            <Card title="Region & dates">
              <div className="settings-row">
                <label className="settings-label" htmlFor={`${ids}-region`}>Region</label>
                <p className="settings-hint" id={`${ids}-region-hint`}>Sets the language for month and weekday names and the regional default date style.</p>
                <select id={`${ids}-region`} className="field" aria-describedby={`${ids}-region-hint`} value={prefs.region} onChange={(e) => setRegion(e.target.value)}>
                  {REGIONS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </div>
              <div className="settings-row">
                <label className="settings-label" htmlFor={`${ids}-datefmt`}>Date format</label>
                <p className="settings-hint" id={`${ids}-datefmt-hint`}>How every date in the app is written: lists, boards, the schedule, history, exports and prints.</p>
                <select id={`${ids}-datefmt`} className="field" aria-describedby={`${ids}-datefmt-hint`} value={prefs.dateFormat} onChange={(e) => setDateFormat(e.target.value as DateFmt)}>
                  {DATE_FORMATS.map((d) => (
                    <option key={d.id} value={d.id}>{d.name} - {sampleDate(prefs.region, d.id)}</option>
                  ))}
                </select>
              </div>
              <div className="settings-row">
                <p className="settings-label" id={`${ids}-preview`}>Preview</p>
                <dl className="date-preview" aria-labelledby={`${ids}-preview`}>
                  <div className="date-preview-row"><dt>Today</dt><dd>{df.date(now)}</dd></div>
                  <div className="date-preview-row"><dt>A delivery date</dt><dd>{df.date(soon, { weekday: 'short' })}</dd></div>
                  <div className="date-preview-row"><dt>A change, with its time</dt><dd>{df.dateTime(now)}</dd></div>
                  <div className="date-preview-row"><dt>A date range</dt><dd>{df.range(now, soon)}</dd></div>
                </dl>
              </div>
            </Card>

            <Card title="Display">
              <ChoiceRow legend="Page titles" hint="Show the big title at the top of each page, or hide it for a more compact view.">
                <SegRadio
                  name="page-titles"
                  value={prefs.hideHeaders ? 'hidden' : 'shown'}
                  options={[{ id: 'shown', label: 'Shown' }, { id: 'hidden', label: 'Hidden' }]}
                  onChange={(v) => setHideHeaders(v === 'hidden')}
                />
              </ChoiceRow>
              {uiTheme.cursorHalo ? (
                <ChoiceRow legend="Cursor halo" hint={<>A soft highlight that follows your mouse. Turn it off for a plainer view.{!canHover && ' Only shows with a mouse or trackpad.'}</>}>
                  <SegRadio
                    name="cursor-halo"
                    value={prefs.cursorHalo ? 'on' : 'off'}
                    options={[{ id: 'on', label: 'On' }, { id: 'off', label: 'Off' }]}
                    onChange={(v) => setCursorHalo(v === 'on')}
                  />
                </ChoiceRow>
              ) : (
                <NoteRow label="Cursor halo">Off in this theme. Your choice comes back when you switch theme.</NoteRow>
              )}
              <ChoiceRow legend="Cell history" hint={<>The card that shows who changed a cell and when, as you rest the pointer on a cell of the Overview, Service, GRP, PDI, DS or Deck, or on a HIN. Turn it off if it gets in the way.{!canHover && ' Only shows with a mouse or trackpad.'}</>}>
                <SegRadio
                  name="cell-history"
                  value={prefs.cellHistory ? 'on' : 'off'}
                  options={[{ id: 'on', label: 'On' }, { id: 'off', label: 'Off' }]}
                  onChange={(v) => setCellHistory(v === 'on')}
                />
              </ChoiceRow>
              <ChoiceRow legend="Hover tips" hint="The small cards that explain something as you rest the pointer on it, such as what a Service status means.">
                <SegRadio
                  name="hover-tips"
                  value={prefs.hoverTips ? 'on' : 'off'}
                  options={[{ id: 'on', label: 'On' }, { id: 'off', label: 'Off' }]}
                  onChange={(v) => setHoverTips(v === 'on')}
                />
              </ChoiceRow>
            </Card>

            {/* on phones this card comes first (CSS order), so the profile and Log out aren't a long scroll down */}
            <Card title="Account" className="settings-card--account">
              <div className="settings-row settings-account">
                <div className="settings-profile">
                  <Avatar name={me.user.displayName} avatar={me.user.avatar} size={44} />
                  <div style={{ minWidth: 0 }}>
                    <div className="settings-profile-name">{me.user.displayName}</div>
                    <div className="muted settings-profile-meta" style={{ overflowWrap: 'anywhere' }}>{me.user.email} · {roleLabel(me.user.role)}</div>
                  </div>
                </div>
                <div className="settings-account-actions">
                  <button className="btn" onClick={() => setEditProfile(true)}>Edit profile</button>
                  {isAdmin && <Link className="btn" to="/admin">Administration</Link>}
                  <button className="btn btn-danger" onClick={() => void logout()}>Log out</button>
                </div>
              </div>
              <MyGoogleAccount rolodexEmail={me.user.email} />
            </Card>

            <Card title="Tickets" anchor="tickets" className="settings-card--tickets">
              <MyTickets />
            </Card>
          </div>
        </div>
      </div>

      {editProfile && <ProfileModal me={me} onClose={() => setEditProfile(false)} />}
    </>
  );
}
